"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectCatalogDateAttributeToDataset = exports.selectAllCatalogMeasuresMap = exports.selectAllCatalogAttributesMap = exports.selectAllCatalogDisplayFormsMap = exports.selectAllCatalogDateDatasetsMap = exports.selectAttributesWithDisplayFormLink = exports.selectAttributesWithDrillDown = exports.selectCatalogDateAttributes = exports.selectHasCatalogDateDatasets = exports.selectCatalogDateDatasets = exports.selectHasCatalogFacts = exports.selectCatalogFacts = exports.selectHasCatalogMeasures = exports.selectCatalogMeasures = exports.selectCatalogAttributeDisplayForms = exports.selectHasCatalogAttributes = exports.selectCatalogAttributes = void 0;
var tslib_1 = require("tslib");
// (C) 2021-2022 GoodData Corporation
var toolkit_1 = require("@reduxjs/toolkit");
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var dateAttributeWithDatasetMap_1 = require("../../../_staging/catalog/dateAttributeWithDatasetMap");
var objRefMap_1 = require("../../../_staging/metadata/objRefMap");
var backendCapabilitiesSelectors_1 = require("../backendCapabilities/backendCapabilitiesSelectors");
var displayFormMap_1 = require("../../../_staging/catalog/displayFormMap");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var negate_1 = tslib_1.__importDefault(require("lodash/negate"));
var selectSelf = toolkit_1.createSelector(function (state) { return state; }, function (state) { return state.catalog; });
/**
 * @public
 */
exports.selectCatalogAttributes = toolkit_1.createSelector(selectSelf, function (state) {
    var _a;
    return (_a = state.attributes) !== null && _a !== void 0 ? _a : [];
});
/**
 * @alpha
 */
exports.selectHasCatalogAttributes = toolkit_1.createSelector(exports.selectCatalogAttributes, negate_1.default(isEmpty_1.default));
/**
 * @public
 */
exports.selectCatalogAttributeDisplayForms = toolkit_1.createSelector(exports.selectCatalogAttributes, function (attributes) {
    return flatMap_1.default(attributes, function (attribute) { return tslib_1.__spread(attribute.displayForms, attribute.geoPinDisplayForms); });
});
/**
 * @public
 */
exports.selectCatalogMeasures = toolkit_1.createSelector(selectSelf, function (state) {
    var _a;
    return (_a = state.measures) !== null && _a !== void 0 ? _a : [];
});
/**
 * @alpha
 */
exports.selectHasCatalogMeasures = toolkit_1.createSelector(exports.selectCatalogMeasures, negate_1.default(isEmpty_1.default));
/**
 * @public
 */
exports.selectCatalogFacts = toolkit_1.createSelector(selectSelf, function (state) {
    var _a;
    return (_a = state.facts) !== null && _a !== void 0 ? _a : [];
});
/**
 * @alpha
 */
exports.selectHasCatalogFacts = toolkit_1.createSelector(exports.selectCatalogFacts, negate_1.default(isEmpty_1.default));
/**
 * @public
 */
exports.selectCatalogDateDatasets = toolkit_1.createSelector(selectSelf, function (state) {
    var _a;
    return (_a = state.dateDatasets) !== null && _a !== void 0 ? _a : [];
});
/**
 * @alpha
 */
exports.selectHasCatalogDateDatasets = toolkit_1.createSelector(exports.selectCatalogDateDatasets, negate_1.default(isEmpty_1.default));
/**
 * @public
 */
exports.selectCatalogDateAttributes = toolkit_1.createSelector(exports.selectCatalogDateDatasets, function (dateDatasets) {
    return flatMap_1.default(dateDatasets, function (dd) { return dd.dateAttributes; });
});
/**
 * @alpha
 */
exports.selectAttributesWithDrillDown = toolkit_1.createSelector([exports.selectCatalogAttributes, exports.selectCatalogDateAttributes], function (attributes, dateAttributes) {
    if (attributes === void 0) { attributes = []; }
    if (dateAttributes === void 0) { dateAttributes = []; }
    return tslib_1.__spread(attributes, dateAttributes).filter(function (attr) { return attr.attribute.drillDownStep; });
});
/**
 * @internal
 */
exports.selectAttributesWithDisplayFormLink = toolkit_1.createSelector([exports.selectCatalogAttributes], function (attributes) {
    if (attributes === void 0) { attributes = []; }
    return attributes.filter(function (attr) { return attr.attribute.drillToAttributeLink; });
});
/**
 * Selects all date datasets in the catalog as a mapping of obj ref to date dataset.
 *
 * @alpha
 */
exports.selectAllCatalogDateDatasetsMap = toolkit_1.createSelector([exports.selectCatalogDateDatasets, backendCapabilitiesSelectors_1.selectBackendCapabilities], function (dateDatasets, capabilities) {
    return objRefMap_1.newCatalogDateDatasetMap(dateDatasets, capabilities.hasTypeScopedIdentifiers);
});
/**
 * Selects all display forms in the catalog as a mapping of obj ref to display form
 *
 * @alpha
 */
exports.selectAllCatalogDisplayFormsMap = toolkit_1.createSelector([exports.selectCatalogAttributes, exports.selectCatalogDateDatasets, backendCapabilitiesSelectors_1.selectBackendCapabilities], function (attributes, dateDatasets, capabilities) {
    return displayFormMap_1.createDisplayFormMap(attributes, dateDatasets, capabilities.hasTypeScopedIdentifiers);
});
/**
 * Selects all attributes in the catalog as a mapping of ref to catalog's attribute object. The mapping
 * will include both 'normal' attributes and attributes from date datasets.
 *
 * @remarks see `isCatalogAttribute` guard; this can be used to determine type of attribute
 * @alpha
 */
exports.selectAllCatalogAttributesMap = toolkit_1.createSelector([exports.selectCatalogAttributes, exports.selectCatalogDateDatasets, backendCapabilitiesSelectors_1.selectBackendCapabilities], function (attributes, dateDatasets, capabilities) {
    var dateAttributes = flatMap_1.default(dateDatasets, function (d) { return d.dateAttributes; });
    return objRefMap_1.newCatalogAttributeMap(tslib_1.__spread(attributes, dateAttributes), capabilities.hasTypeScopedIdentifiers);
});
/**
 * Selects all measures in the catalog as a mapping of ref to catalog's measure object.
 *
 * @alpha
 */
exports.selectAllCatalogMeasuresMap = toolkit_1.createSelector([exports.selectCatalogMeasures, backendCapabilitiesSelectors_1.selectBackendCapabilities], function (measures, capabilities) {
    return objRefMap_1.newCatalogMeasureMap(measures, capabilities.hasTypeScopedIdentifiers);
});
/**
 * Selects lookup mapping between date dataset attributes and date datasets. The entry in lookup contains both the date dataset attribute
 * and the date dataset to which it belongs. The lookup is indexed by the date dataset attribute and entries can be obtained using
 * attribute refs.
 *
 * @alpha
 */
exports.selectCatalogDateAttributeToDataset = toolkit_1.createSelector([exports.selectCatalogDateDatasets, backendCapabilitiesSelectors_1.selectBackendCapabilities], function (dateDatasets, capabilities) {
    var attributesWithDatasets = flatMap_1.default(dateDatasets, function (dataset) {
        return dataset.dateAttributes.map(function (attribute) {
            return {
                attribute: attribute,
                dataset: dataset,
            };
        });
    });
    return dateAttributeWithDatasetMap_1.newCatalogDateAttributeWithDatasetMap(attributesWithDatasets, capabilities.hasTypeScopedIdentifiers);
});
//# sourceMappingURL=catalogSelectors.js.map